import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
} from "reactstrap";

// Import services
import apiService from '../../../service/apiService';
import apiConfig, { buildEndpointUrl } from '../../../config/apiConfig';
import AutocompleteInput from '../../../components/AutocompleteInput';
import locationService from '../../../service/locationService';
import { ContactFormModal, AddressFormModal } from '../components/shared';
import ConfirmationModal from '../../../components/ConfirmationModal';
import {
  validateContactEmail,
  validateContactPhone,
  filterContactPhone,
  validateBirthDate,
  validateOwnerName,
  validateCitizenship,
  validateOwnershipPercentage,
  validateSSN,
  filterOwnerName,
  filterCitizenship,
  filterOwnershipPercentage
} from 'modules/InputsValidations';

const AddOwner = ({ 
  isOpen = false, 
  toggle = () => {}, 
  merchantId = null, 
  onOwnerAdded = () => {} 
}) => {
  // States for owner form
  const [newOwner, setNewOwner] = useState({
    owner: {
      firstName: '',
      middleName: '',
      lastName: '',
      gender: 'MALE',
      birthDate: '',
      citizenship: '',
      ownershipPercentage: '',
      socialSecurityNumber: ''
    },
    contacts: [],
    addresses: []
  });
  
  const [addingOwner, setAddingOwner] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorModalMessage, setErrorModalMessage] = useState('');
  
  // States for contact and address modals
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  
  // States for contact management
  const [emailContacts, setEmailContacts] = useState([
    { id: 1, value: '', isPrimary: true }
  ]);
  const [phoneContacts, setPhoneContacts] = useState([
    { id: 1, value: '', isPrimary: true }
  ]);
  const [contactErrors, setContactErrors] = useState({});
  
  // States for confirmation modal
  const [showDeleteAddressModal, setShowDeleteAddressModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  
  // States for success/error modals
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setNewOwner({
      owner: {
        firstName: '',
        middleName: '',
        lastName: '',
        gender: 'MALE',
        birthDate: '',
        citizenship: '',
        ownershipPercentage: '',
        socialSecurityNumber: ''
      },
      contacts: [],
      addresses: []
    });
    setErrors({});
    setErrorModalMessage('');
    setShowContactModal(false);
    setShowAddressModal(false);
    setEditingContactIndex(null);
    setEditingAddressIndex(null);
    setEmailContacts([{ id: 1, value: '', isPrimary: true }]);
    setPhoneContacts([{ id: 1, value: '', isPrimary: true }]);
    setContactErrors({});
    setShowDeleteAddressModal(false);
    setAddressToDelete(null);
    setShowSuccessModal(false);
    setShowErrorModal(false);
    setErrorMessage('');
  };

  // Functions for contact management
  const handleEmailChange = (id, value) => {
    // Filter and validate email
    const validation = validateContactEmail(value, 'Email');
    
    setEmailContacts(prev => prev.map(contact => 
      contact.id === id ? { ...contact, value } : contact
    ));
    
    // Update errors
    if (!validation.isValid) {
      setContactErrors(prev => ({ ...prev, [`email_${id}`]: validation.message }));
    } else {
      setContactErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`email_${id}`];
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (id, value) => {
    // Filter phone numbers to only allow digits
    const filteredValue = filterContactPhone(value);
    const validation = validateContactPhone(filteredValue, 'Phone');
    
    setPhoneContacts(prev => prev.map(contact => 
      contact.id === id ? { ...contact, value: filteredValue } : contact
    ));
    
    // Update errors
    if (!validation.isValid) {
      setContactErrors(prev => ({ ...prev, [`phone_${id}`]: validation.message }));
    } else {
      setContactErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`phone_${id}`];
        return newErrors;
      });
    }
  };

  const setPrimaryEmail = (id) => {
    setEmailContacts(prev => prev.map(contact => ({
      ...contact,
      isPrimary: contact.id === id
    })));
  };

  const setPrimaryPhone = (id) => {
    setPhoneContacts(prev => prev.map(contact => ({
      ...contact,
      isPrimary: contact.id === id
    })));
  };

  const addEmailContact = () => {
    const newId = Math.max(...emailContacts.map(c => c.id)) + 1;
    setEmailContacts(prev => [...prev, { id: newId, value: '', isPrimary: false }]);
  };

  const addPhoneContact = () => {
    const newId = Math.max(...phoneContacts.map(c => c.id)) + 1;
    setPhoneContacts(prev => [...prev, { id: newId, value: '', isPrimary: false }]);
  };

  const removeEmailContact = (id) => {
    if (emailContacts.length > 1) {
      setEmailContacts(prev => {
        const updated = prev.filter(contact => contact.id !== id);
        // If we removed the primary, make the first remaining one primary
        if (updated.length > 0 && !updated.some(contact => contact.isPrimary)) {
          updated[0].isPrimary = true;
        }
        return updated;
      });
      // Clear error for removed contact
      setContactErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`email_${id}`];
        return newErrors;
      });
    }
  };

  const removePhoneContact = (id) => {
    if (phoneContacts.length > 1) {
      setPhoneContacts(prev => {
        const updated = prev.filter(contact => contact.id !== id);
        // If we removed the primary, make the first remaining one primary
        if (updated.length > 0 && !updated.some(contact => contact.isPrimary)) {
          updated[0].isPrimary = true;
        }
        return updated;
      });
      // Clear error for removed contact
      setContactErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`phone_${id}`];
        return newErrors;
      });
    }
  };

  // Handle owner input changes with validation
  const handleOwnerChange = (field, value) => {
    let processedValue = value;
    
    // Apply filters for specific fields (like wizard)
    if (field === 'firstName' || field === 'middleName' || field === 'lastName') {
      processedValue = filterOwnerName(value);
    } else if (field === 'citizenship') {
      processedValue = filterCitizenship(value);
    } else if (field === 'ownershipPercentage') {
      processedValue = filterOwnershipPercentage(value);
    } else if (field === 'socialSecurityNumber') {
      // Format SSN automatically (like wizard)
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 3) {
        processedValue = digits;
      } else if (digits.length <= 5) {
        processedValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        processedValue = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
      }
    } else if (field === 'birthDate') {
      // Validate birth date for minimum 18 years old
      if (processedValue) {
        const birthDateValidation = validateBirthDate(processedValue, 'Birth Date');
        if (!birthDateValidation.isValid) {
          setErrors(prev => ({ ...prev, birthDate: birthDateValidation.message }));
          return;
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.birthDate;
            return newErrors;
          });
        }
      }
    }

    setNewOwner(prev => ({
      ...prev,
      owner: {
        ...prev.owner,
        [field]: processedValue
      }
    }));

    // Real-time validation for name fields (like wizard)
    if (field === 'firstName' || field === 'middleName' || field === 'lastName') {
      const isRequired = field === 'firstName' || field === 'lastName';
      const fieldName = field === 'firstName' ? 'First name' : field === 'middleName' ? 'Middle name' : 'Last name';
      
      const validationResult = validateOwnerName(processedValue, fieldName, isRequired);
      
      if (validationResult.isValid) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        setErrors(prev => ({
          ...prev,
          [field]: validationResult.message
        }));
      }
    } else if (field === 'citizenship') {
      const validationResult = validateCitizenship(processedValue, 'Citizenship');
      
      if (validationResult.isValid) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        setErrors(prev => ({
          ...prev,
          [field]: validationResult.message
        }));
      }
    } else if (field === 'ownershipPercentage') {
      const validationResult = validateOwnershipPercentage(processedValue, 'Ownership Percentage');
      
      if (validationResult.isValid) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        setErrors(prev => ({
          ...prev,
          [field]: validationResult.message
        }));
      }
    } else if (field === 'socialSecurityNumber') {
      const validationResult = validateSSN(processedValue, 'Social Security Number');
      
      if (validationResult.isValid) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        setErrors(prev => ({
          ...prev,
          [field]: validationResult.message
        }));
      }
    } else {
      // Clear any existing errors for other fields
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle contact modal
  const handleAddContact = () => {
    setEditingContactIndex(null); // Clear editing state
    setShowContactModal(true);
  };

  const handleContactAdded = (newContact) => {
    if (editingContactIndex !== null) {
      // Editing existing contact
      setNewOwner(prev => {
        const updatedContacts = prev.contacts.map((contact, index) => 
          index === editingContactIndex ? newContact : contact
        );
        
        // If setting as primary, remove primary from other contacts of same type
        if (newContact.isPrimary) {
          return {
            ...prev,
            contacts: updatedContacts.map(contact => 
              contact.type === newContact.type && contact !== newContact 
                ? { ...contact, isPrimary: false }
                : contact
            )
          };
        }
        
        return { ...prev, contacts: updatedContacts };
      });
      setEditingContactIndex(null);
    } else {
      // Adding new contact
      setNewOwner(prev => {
        let updatedContacts = [...prev.contacts, newContact];
        
        // If setting as primary, remove primary from other contacts of same type
        if (newContact.isPrimary) {
          updatedContacts = updatedContacts.map(contact => 
            contact.type === newContact.type && contact !== newContact 
              ? { ...contact, isPrimary: false }
              : contact
          );
        }
        
        return { ...prev, contacts: updatedContacts };
      });
    }
    setShowContactModal(false);
  };


  const handleEditContact = (index) => {
    setEditingContactIndex(index);
    setShowContactModal(true);
  };

  // Handle adding address
  const handleAddAddress = () => {
    setEditingAddressIndex(null); // Clear editing state
    setShowAddressModal(true);
  };

  const handleAddressAdded = (addressData) => {
    // For AddOwner modal, we just add the address to the local state
    // The actual API call will be made when saving the owner
    if (editingAddressIndex !== null) {
      // Editing existing address
      setNewOwner(prev => ({
        ...prev,
        addresses: prev.addresses.map((address, index) => 
          index === editingAddressIndex ? addressData : address
        )
      }));
      setEditingAddressIndex(null);
    } else {
      // Adding new address
      setNewOwner(prev => ({
        ...prev,
        addresses: [...prev.addresses, addressData]
      }));
    }
    setShowAddressModal(false);
  };

  const handleRemoveAddress = (index) => {
    const address = newOwner.addresses[index];
    setAddressToDelete({ index, address });
    setShowDeleteAddressModal(true);
  };

  const confirmDeleteAddress = () => {
    if (addressToDelete) {
      setNewOwner(prev => ({
        ...prev,
        addresses: prev.addresses.filter((_, i) => i !== addressToDelete.index)
      }));
    }
    setShowDeleteAddressModal(false);
    setAddressToDelete(null);
  };

  const handleEditAddress = (index) => {
    setEditingAddressIndex(index);
    setShowAddressModal(true);
  };

  // Get available address types (allow multiple of same type)
  const getAvailableAddressTypes = () => {
    return ['PHYSICAL', 'MAILING', 'BILLING'];
  };

  // Validate all required fields
  const validateForm = () => {
    const newErrors = {};
    
    // Validate required owner fields
    if (!newOwner.owner.firstName || newOwner.owner.firstName.trim() === '') {
      newErrors.firstName = 'First Name is required';
    }
    
    if (!newOwner.owner.lastName || newOwner.owner.lastName.trim() === '') {
      newErrors.lastName = 'Last Name is required';
    }
    
    if (!newOwner.owner.citizenship || newOwner.owner.citizenship.trim() === '') {
      newErrors.citizenship = 'Citizenship is required';
    }
    
    if (!newOwner.owner.ownershipPercentage || newOwner.owner.ownershipPercentage.trim() === '') {
      newErrors.ownershipPercentage = 'Ownership Percentage is required';
    } else {
      const percentage = parseFloat(newOwner.owner.ownershipPercentage);
      if (isNaN(percentage) || percentage <= 0 || percentage > 100) {
        newErrors.ownershipPercentage = 'Ownership Percentage must be between 1 and 100';
      }
    }
    
    if (!newOwner.owner.socialSecurityNumber || newOwner.owner.socialSecurityNumber.trim() === '') {
      newErrors.socialSecurityNumber = 'SSN is required';
    } else {
      const ssnDigits = newOwner.owner.socialSecurityNumber.replace(/\D/g, '');
      if (ssnDigits.length !== 9) {
        newErrors.socialSecurityNumber = 'SSN must be 9 digits';
      }
    }
    
    if (!newOwner.owner.birthDate || newOwner.owner.birthDate.trim() === '') {
      newErrors.birthDate = 'Birth Date is required';
    } else {
      // Validate birth date format and age
      const birthDateValidation = validateBirthDate(newOwner.owner.birthDate, 'Birth Date');
      if (!birthDateValidation.isValid) {
        newErrors.birthDate = birthDateValidation.message;
      }
    }
    
    // Validate contacts - require at least one primary phone and one primary email with valid format
    const primaryPhoneContacts = phoneContacts.filter(contact => {
      if (!contact.isPrimary || !contact.value || !contact.value.trim()) return false;
      const phoneValidation = validateContactPhone(contact.value, 'Phone');
      return phoneValidation.isValid;
    });
    const primaryEmailContacts = emailContacts.filter(contact => {
      if (!contact.isPrimary || !contact.value || !contact.value.trim()) return false;
      const emailValidation = validateContactEmail(contact.value, 'Email');
      return emailValidation.isValid;
    });
    
    if (primaryPhoneContacts.length === 0) {
      newErrors.contacts = 'At least one primary phone number with valid format is required';
    } else if (primaryEmailContacts.length === 0) {
      newErrors.contacts = 'At least one primary email address with valid format is required';
    }
    
    // Validate addresses
    if (newOwner.addresses.length === 0) {
      newErrors.addresses = 'At least one address is required';
    }
    
    return newErrors;
  };

  // Check if form is complete for button enable/disable
  const isFormComplete = () => {
    // Check required fields
    if (!newOwner.owner.firstName || !newOwner.owner.lastName || !newOwner.owner.gender || 
        !newOwner.owner.citizenship || !newOwner.owner.ownershipPercentage || 
        !newOwner.owner.socialSecurityNumber || !newOwner.owner.birthDate) {
      return false;
    }
    
    // Check if we have at least one primary phone and one primary email contact with valid format
    const hasPrimaryEmail = emailContacts.some(contact => {
      if (!contact.isPrimary || !contact.value.trim()) return false;
      const emailValidation = validateContactEmail(contact.value, 'Email');
      return emailValidation.isValid;
    });
    const hasPrimaryPhone = phoneContacts.some(contact => {
      if (!contact.isPrimary || !contact.value.trim()) return false;
      const phoneValidation = validateContactPhone(contact.value, 'Phone');
      return phoneValidation.isValid;
    });
    
    if (!hasPrimaryEmail || !hasPrimaryPhone) {
      return false;
    }
    
    // Check if we have at least one address
    if (newOwner.addresses.length === 0) {
      return false;
    }
    
    // Check if there are any validation errors
    if (Object.keys(errors).length > 0) {
      return false;
    }
    
    return true;
  };

  // Save new owner
  const handleSaveNewOwner = async () => {
    if (!merchantId) return;

    console.log('🔍 Debugging form state:');
    console.log('- newOwner:', newOwner);
    console.log('- emailContacts:', emailContacts);
    console.log('- phoneContacts:', phoneContacts);
    console.log('- errors:', errors);

    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      console.log('❌ Form validation errors:', formErrors);
      setErrors(formErrors);
      setErrorMessage('Please fix the errors in the form');
      setShowErrorModal(true);
      return;
    }

    setAddingOwner(true);
    try {
      // Combine inline contacts with existing contacts
      const allContacts = [
        ...emailContacts.map(contact => ({
          type: 'EMAIL',
          value: contact.value,
          isPrimary: contact.isPrimary
        })),
        ...phoneContacts.map(contact => ({
          type: 'PHONE',
          value: contact.value,
          isPrimary: contact.isPrimary
        })),
        ...newOwner.contacts
      ];

      // Format birth date to ISO string (YYYY-MM-DD)
      const formatBirthDate = (dateString) => {
        if (!dateString) return null;
        // If it's already in YYYY-MM-DD format, return as is
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateString;
        }
        // If it's in MM/DD/YYYY format, convert to YYYY-MM-DD
        if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [month, day, year] = dateString.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateString;
      };

      const ownerPayload = {
        firstName: newOwner.owner.firstName,
        middleName: newOwner.owner.middleName,
        lastName: newOwner.owner.lastName,
        socialSecurityNumber: newOwner.owner.socialSecurityNumber,
        birthDate: formatBirthDate(newOwner.owner.birthDate),
        gender: newOwner.owner.gender,
        citizenship: newOwner.owner.citizenship,
        ownershipPercentage: parseFloat(newOwner.owner.ownershipPercentage),
        contacts: allContacts,
        addresses: newOwner.addresses
      };

      console.log('📤 Sending owner payload:', ownerPayload);
      
      // ✅ Usar apiService con buildEndpointUrl para la URL correcta
      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.createOwner(merchantId));
      const createdOwner = await apiService.post(endpoint, ownerPayload);
      
      console.log('✅ Owner created successfully:', createdOwner);
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Call parent callback
      if (onOwnerAdded) {
        await onOwnerAdded();
      }
      
      // Close modal and reset form after a short delay
      setTimeout(() => {
        toggle();
        resetForm();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error creating owner:', error);
      setErrorMessage(error.message || 'Error creating owner. Please try again.');
      setShowErrorModal(true);
    } finally {
      setAddingOwner(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
        <ModalHeader toggle={toggle} >
          Add Owner
        </ModalHeader>
        <ModalBody>

          {/* Personal Information Section */}
          <div className="">
            <h6 className="mb-2">
              <i className="ni ni-single-02 mr-2"></i>
              Personal Information
            </h6>
            <Row>
              <Col md="3">
                <FormGroup>
                  <Label htmlFor="firstName">First Name: <span>*</span></Label>
                  <Input 
                    id="firstName" 
                    name="firstName" 
                    type="text" 
                    value={newOwner.owner.firstName} 
                    onChange={(e) => handleOwnerChange('firstName', e.target.value)} 
                    placeholder="Enter owner first name"
                    className={`${errors.firstName ? 'is-invalid' : ''}`}
                    bsSize="sm"
                    maxLength="255"
                  />
                  {errors.firstName && <div className="invalid-feedback d-block">{errors.firstName}</div>}
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label htmlFor="middleName">Middle Name:</Label>
                  <Input 
                    id="middleName" 
                    name="middleName" 
                    type="text" 
                    value={newOwner.owner.middleName} 
                    onChange={(e) => handleOwnerChange('middleName', e.target.value)} 
                    placeholder="Enter owner middle name"
                    bsSize="sm"
                    maxLength="255"
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label htmlFor="lastName">Last Name: <span>*</span></Label>
                  <Input 
                    id="lastName" 
                    name="lastName" 
                    type="text" 
                    value={newOwner.owner.lastName} 
                    onChange={(e) => handleOwnerChange('lastName', e.target.value)} 
                    placeholder="Enter owner last name"
                    className={`${errors.lastName ? 'is-invalid' : ''}`}
                    bsSize="sm"
                    maxLength="255"
                  />
                  {errors.lastName && <div className="invalid-feedback d-block">{errors.lastName}</div>}
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label htmlFor="gender">Gender:</Label>
                  <Input 
                    id="gender" 
                    name="gender" 
                    type="select" 
                    value={newOwner.owner.gender} 
                    onChange={(e) => handleOwnerChange('gender', e.target.value)}
                    bsSize="sm"
                  >
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="3">
                <FormGroup>
                  <Label htmlFor="socialSecurityNumber">SSN: <span>*</span></Label>
                  <Input 
                    id="socialSecurityNumber" 
                    name="socialSecurityNumber" 
                    type="text" 
                    value={newOwner.owner.socialSecurityNumber} 
                    onChange={(e) => handleOwnerChange('socialSecurityNumber', e.target.value)} 
                    placeholder="Enter Social Security Number"
                    className={`${errors.socialSecurityNumber ? 'is-invalid' : ''}`}
                    bsSize="sm"
                  />
                  <small className="form-text text-muted">Format: 123-45-6789</small>
                  {errors.socialSecurityNumber && <div className="invalid-feedback d-block">{errors.socialSecurityNumber}</div>}
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label htmlFor="birthDate">Date of Birth: <span>*</span></Label>
                  <Input 
                    id="birthDate" 
                    name="birthDate" 
                    type="date" 
                    value={newOwner.owner.birthDate} 
                    onChange={(e) => handleOwnerChange('birthDate', e.target.value)} 
                    placeholder="dd/mm/aaaa"
                    className={`${errors.birthDate ? 'is-invalid' : ''}`}
                    bsSize="sm"
                  />
                  {errors.birthDate && <div className="invalid-feedback d-block">{errors.birthDate}</div>}
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label htmlFor="citizenship">Citizenship: <span>*</span></Label>
                  <Input 
                    id="citizenship" 
                    name="citizenship" 
                    type="text" 
                    value={newOwner.owner.citizenship} 
                    onChange={(e) => handleOwnerChange('citizenship', e.target.value)} 
                    placeholder="Enter owner citizenship"
                    className={`${errors.citizenship ? 'is-invalid' : ''}`}
                    bsSize="sm"
                    maxLength="10"
                  />
                  {errors.citizenship && <div className="invalid-feedback d-block">{errors.citizenship}</div>}
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label htmlFor="ownershipPercentage">Ownership %: <span>*</span></Label>
                  <Input 
                    id="ownershipPercentage" 
                    name="ownershipPercentage" 
                    type="text" 
                    value={newOwner.owner.ownershipPercentage} 
                    onChange={(e) => handleOwnerChange('ownershipPercentage', e.target.value)} 
                    placeholder="Enter ownership percentage (1-100)"
                    className={`${errors.ownershipPercentage ? 'is-invalid' : ''}`}
                    bsSize="sm"
                  />
                  <small className="form-text text-muted">Enter a value between 1 and 100</small>
                  {errors.ownershipPercentage && <div className="invalid-feedback d-block">{errors.ownershipPercentage}</div>}
                </FormGroup>
              </Col>
            </Row>
          </div>

          {/* Communication Methods Section */}
          <div className="mb-4">
            <h6 className="mb-3">
              <i className="ni ni-mobile-button mr-2"></i>
              Communication Methods
            </h6>
            
            <Row>
              {/* Email Addresses Column */}
              <Col md="6">
                <Label>Email Addresses <span>*</span></Label>
                
                {/* Email Contacts */}
                {emailContacts.map((contact, index) => (
                  <div key={contact.id} className="d-flex align-items-center mb-2">
                    <Input
                      type="email"
                      value={contact.value}
                      onChange={(e) => handleEmailChange(contact.id, e.target.value)}
                      placeholder="Enter email address"
                      className={`mr-2 ${contactErrors[`email_${contact.id}`] ? 'is-invalid' : ''}`}
                      bsSize="sm"
                      style={{ width: '60%' }}
                    />
                    <div className="d-flex align-items-center mr-2">
                      <Label className="mb-0 mr-2">Primary?</Label>
                      <div className="form-check form-check-inline">
                        <Input
                          type="radio"
                          name={`emailPrimary_${contact.id}`}
                          checked={contact.isPrimary}
                          onChange={() => setPrimaryEmail(contact.id)}
                          className="form-check-input"
                        />
                        <Label className="form-check-label">Yes</Label>
                      </div>
                      <div className="form-check form-check-inline">
                        <Input
                          type="radio"
                          name={`emailPrimary_${contact.id}`}
                          checked={!contact.isPrimary}
                          onChange={() => setPrimaryEmail(contact.id)}
                          className="form-check-input"
                        />
                        <Label className="form-check-label">No</Label>
                      </div>
                    </div>
                    {emailContacts.length > 1 && (
                      <Button 
                        color="danger" 
                        size="sm" 
                        onClick={() => removeEmailContact(contact.id)}
                      >
                        <i className="ni ni-fat-remove"></i>
                      </Button>
                    )}
                  </div>
                ))}
                
                {contactErrors[`email_${emailContacts[0]?.id}`] && (
                  <div className="text-danger small mb-2">{contactErrors[`email_${emailContacts[0]?.id}`]}</div>
                )}
                
                <Button color="outline-primary" size="sm" onClick={addEmailContact}>
                  <i className="ni ni-fat-add mr-1"></i>
                   Add email
                </Button>
              </Col>
              
              {/* Phone Numbers Column */}
              <Col md="6">
                <Label>Phone Numbers <span>*</span></Label>
                
                {/* Phone Contacts */}
                {phoneContacts.map((contact, index) => (
                  <div key={contact.id} className="d-flex align-items-center mb-2">
                    <Input
                      type="tel"
                      value={contact.value}
                      onChange={(e) => handlePhoneChange(contact.id, e.target.value)}
                      placeholder="Enter phone number"
                      className={`mr-2 ${contactErrors[`phone_${contact.id}`] ? 'is-invalid' : ''}`}
                      bsSize="sm"
                      style={{ width: '60%' }}
                    />
                    <div className="d-flex align-items-center mr-2">
                      <Label className="mb-0 mr-2">Primary?</Label>
                      <div className="form-check form-check-inline">
                        <Input
                          type="radio"
                          name={`phonePrimary_${contact.id}`}
                          checked={contact.isPrimary}
                          onChange={() => setPrimaryPhone(contact.id)}
                          className="form-check-input"
                        />
                        <Label className="form-check-label">Yes</Label>
                      </div>
                      <div className="form-check form-check-inline">
                        <Input
                          type="radio"
                          name={`phonePrimary_${contact.id}`}
                          checked={!contact.isPrimary}
                          onChange={() => setPrimaryPhone(contact.id)}
                          className="form-check-input"
                        />
                        <Label className="form-check-label">No</Label>
                      </div>
                    </div>
                    {phoneContacts.length > 1 && (
                      <Button 
                        color="danger" 
                        size="sm" 
                        onClick={() => removePhoneContact(contact.id)}
                      >
                        <i className="ni ni-fat-remove"></i>
                      </Button>
                    )}
                  </div>
                ))}
                
                {contactErrors[`phone_${phoneContacts[0]?.id}`] && (
                  <div className="text-danger small mb-2">{contactErrors[`phone_${phoneContacts[0]?.id}`]}</div>
                )}
                
                <Button color="outline-primary" size="sm" onClick={addPhoneContact}>
                  <i className="ni ni-fat-add mr-1"></i>
                   Add phone number
                </Button>
              </Col>
            </Row>
          </div>

          {/* Addresses Section */}
          <div className="mb-4">
            <h6 className="mb-3">
              <i className="ni ni-pin-3 mr-2"></i>
              Addresses
            </h6>
            
            {newOwner.addresses && newOwner.addresses.length > 0 ? (
              <ul className="list-group my-2">
                {newOwner.addresses.map((address, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <h6 className='text-primary mb-1'>{address.addressType}</h6>
                      <p className='fw-bolder text-dark mb-1'>{address.street1}</p>
                      {address.street2 && (
                        <p className='text-muted mb-1'>{address.street2}</p>
                      )}
                      <p className='mb-0 text-muted'>
                        {[address.city, address.state, address.zipCode, address.country]
                          .filter(field => field && field.trim() !== '')
                          .join(', ')}
                      </p>
                    </div>

                    <div className="btn-group">
                      <Button 
                        color="primary"
                        size='sm'
                        onClick={() => {
                          setEditingAddressIndex(index);
                          setShowAddressModal(true);
                        }}>
                        <i className='ni ni-settings-gear-65'></i>
                      </Button>
                      <Button 
                        color="secondary" 
                        size='sm'
                        onClick={() => handleRemoveAddress(index)}>
                        <i className='ni ni-fat-remove'></i>
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div>
                <p className='text-muted mb-3'>Press the button below to add an address (at least one is required).</p>
              </div>
            )}
            
            <Button color="outline-primary" size="sm" onClick={handleAddAddress}>
              <i className="ni ni-fat-add mr-1"></i>
               Add address
            </Button>
          </div>

        </ModalBody>
        
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
          <Button color="primary" onClick={handleSaveNewOwner} disabled={addingOwner || !isFormComplete()}>
            <i className="ni ni-check-bold mr-2"></i>
            Save owner
          </Button>
        </ModalFooter>
      </Modal>

      {/* Error Modal */}
      <Modal isOpen={!!errorModalMessage} toggle={() => setErrorModalMessage('')} size="sm">
        <ModalHeader toggle={() => setErrorModalMessage('')}>Error</ModalHeader>
        <ModalBody>{errorModalMessage}</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setErrorModalMessage('')}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Contact Modal */}
      <ContactFormModal
        isOpen={showContactModal}
        toggle={() => setShowContactModal(false)}
        onSave={handleContactAdded}
        contactData={editingContactIndex !== null ? newOwner.contacts[editingContactIndex] : null}
        existingContacts={newOwner.contacts}
        title="Add Owner Contact"
      />

      {/* Address Modal */}
      <AddressFormModal
        isOpen={showAddressModal}
        toggle={() => setShowAddressModal(false)}
        onSave={handleAddressAdded}
        availableAddressTypes={getAvailableAddressTypes()}
        addressData={editingAddressIndex !== null ? newOwner.addresses[editingAddressIndex] : null}
        showPhoneNumber={false}
        title="Add Owner Address"
        // Note: For AddOwner modal, we don't use API mode because the address will be saved
        // as part of the owner creation process, not as a separate API call
      />

      {/* Delete Address Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteAddressModal}
        toggle={() => setShowDeleteAddressModal(false)}
        title="Delete Address"
        message={addressToDelete ? `Are you sure you want to delete this ${addressToDelete.address.addressType.toLowerCase()} address?` : ''}
        confirmText="Delete"
        confirmColor="danger"
        onConfirm={confirmDeleteAddress}
        icon="ni ni-fat-remove"
      />

      {/* Success Modal */}
      <ConfirmationModal
        isOpen={showSuccessModal}
        toggle={() => setShowSuccessModal(false)}
        title="Success"
        message="Owner created successfully!"
        confirmText="OK"
        confirmColor="success"
        icon="ni ni-check-bold"
      />

      {/* Error Modal */}
      <ConfirmationModal
        isOpen={showErrorModal}
        toggle={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        confirmColor="danger"
        icon="ni ni-fat-remove"
      />
    </>
  );
};

export default AddOwner;
